/**
 * Avatar helpers aligned with WebRTC (Webex Web Client) implementation.
 * For a single participant, the SDK batches multiple size requests into one network call.
 * @see WebRTC requestParticipantAvatar in src/lib/sdk.js
 */

/** Sizes requested in one batch; SDK combines into a single HTTP request. */
const AVATAR_SIZES = [40, 80, 135, 192] as const;

/** Default options matching WebRTC (hideDefaultAvatar: true). */
const DEFAULT_OPTIONS = { hideDefaultAvatar: true };
const avatarRequestCache = new Map<string, Promise<string | undefined>>();

export type AvatarSize = (typeof AVATAR_SIZES)[number];

/**
 * Request participant avatar URLs for multiple sizes.
 * The SDK automatically batches these into one network call per participant.
 * @param participantId - Webex user/participant ID
 * @param options - optional { hideDefaultAvatar, size } (size filters which URL to return if you only need one)
 * @returns Promise of url for the first successful size, or undefined on failure
 */
export function requestParticipantAvatarUrl(
  participantId: string,
  options: { hideDefaultAvatar?: boolean; size?: AvatarSize } = {}
): Promise<string | undefined> {
  const webex = (window as any)?.webex;
  if (!webex?.internal?.avatar?.retrieveAvatarUrl) {
    return Promise.resolve(undefined);
  }
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const cacheKey = `${participantId}|${opts.size ?? 'any'}|${String(
    opts.hideDefaultAvatar
  )}`;
  const cachedRequest = avatarRequestCache.get(cacheKey);
  if (cachedRequest) {
    return cachedRequest;
  }
  // Request all sizes in parallel; SDK batches into one HTTP request
  const request = Promise.all(
    AVATAR_SIZES.map((size) =>
      webex.internal.avatar
        .retrieveAvatarUrl(participantId, { size, ...opts })
        .then((url: string) => ({ size, url }))
        .catch(() => ({ size, url: undefined as string | undefined }))
    )
  )
    .then((results) => {
      const preferred = opts.size && AVATAR_SIZES.includes(opts.size as AvatarSize)
        ? results.find((r) => r.size === opts.size)?.url
        : undefined;
      return preferred ?? results.find((r) => r.url)?.url;
    })
    .catch(() => undefined)
    .finally(() => {
      avatarRequestCache.delete(cacheKey);
    });
  avatarRequestCache.set(cacheKey, request);
  return request;
}
